import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useMediaPermissions } from '../hooks';
import { useVideoSession } from '../context/liveSession';
import { useDeviceSelection } from '../hooks/useDeviceSelection';
import { MediaErrorPrompt } from '../components/MediaErrorPrompt';
import { apiClient } from '../services/api';
import { env } from '../config/env';
import { getBrowserName, getBrowserInstructions } from '../lib/browserPermissions';
import { VideoIcon, MicIcon } from '../components/icons';
import type { CommunicationMode } from '../types/app';
import type { SellerInfo } from '../types/api';
import type { MediaRequest } from '../providers/types';
import { PROMPT_COPY } from '../config/copy';

interface TransitionScreenProps {
  seller: SellerInfo | null;
  videoToken: string;
  roomName: string;
  roomKey: string;
  recordingEnabled: boolean;
  /**
   * The recording-consent answer Portal already holds for this customer, null when
   * they have never been asked. Seeded from the join response, so a second device
   * or a returning tab is not asked a question that has an answer.
   */
  initialConsent?: boolean | null;
  selectedMode: CommunicationMode;
  onReady: (resolvedMode?: CommunicationMode, consented?: boolean) => void;
}

// Hard upper bound (ms) on how long the client may sit on the connection ceremony
// before the watchdog forces a default-device connect. Kept above the 4s
// enumeration timeout (useDeviceSelection) so the normal path resolves first, and
// below any user's patience threshold so a stall is recovered promptly.
const CONNECT_WATCHDOG_MS = 7_000;


export function TransitionScreen({
  seller: _seller,
  videoToken,
  roomName,
  roomKey,
  recordingEnabled,
  initialConsent = null,
  selectedMode,
  onReady,
}: TransitionScreenProps) {
  const { connect, remoteParticipants, error: videoError } = useVideoSession();
  // Ask for exactly what the chosen format needs. A voice consultation promises
  // on this very screen that the camera stays off, and asking for one anyway put
  // a live camera preview inside the browser dialog under that sentence. Chat
  // still asks for both: its copy offers the grant up front so a later switch to
  // voice or video does not stop to ask.
  const requiredDevices: MediaRequest = selectedMode === 'audio' ? 'microphone' : 'both';
  const permissions = useMediaPermissions(requiredDevices);
  const [hasConnected, setHasConnected] = useState(false);
  // Tracks the degraded mode when auto-fallback occurs (camera denied, mic granted)
  const [resolvedMode, setResolvedMode] = useState<CommunicationMode | null>(null);
  // Brief "access granted" confirmation shown after a successful requestPermissions()
  const [showGrantedConfirmation, setShowGrantedConfirmation] = useState(false);

  // Whether the user has clicked "Continue" on the device picker
  const [userConfirmed, setUserConfirmed] = useState(false);

  // A chat customer who took the screen at its word and moved on without
  // granting anything. The screen says the camera and microphone are optional
  // for chat, and until this existed that sentence was not true: the only button
  // asked the browser, and a chat customer could reach the conversation only by
  // answering the dialog — or by closing it three times, earning Chrome's
  // seven-day block, and arriving through the "blocked" screen.
  const [permissionsSkipped, setPermissionsSkipped] = useState(false);

  // Recording-consent state. The initial value is Portal's answer, not a blank:
  // the question is only asked when nobody knows the answer yet.
  const needsConsent = recordingEnabled && !env.demoMode;
  const [consentDecision, setConsentDecision] = useState<boolean | null>(initialConsent);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  // Whether the answer is one Portal actually holds. Seeded true for an answer that
  // came from Portal in the first place.
  const [consentLanded, setConsentLanded] = useState(initialConsent !== null);

  // The join response can still be in flight when this screen mounts. Adopt a late
  // answer rather than asking over it — but never overwrite one taken here, which is
  // by definition the newer of the two.
  useEffect(() => {
    if (initialConsent === null || consentDecision !== null) return;
    setConsentDecision(initialConsent);
    setConsentLanded(true);
  }, [initialConsent, consentDecision]);

  // What the rest of the app may be told the answer is. Portal builds the recording
  // rules, so an answer whose POST failed is not in force however the customer
  // answered — reporting it anyway would light "your voice is being recorded" over a
  // voice Portal is in fact excluding. The customer is not asked again for it: they
  // answered, and the server's "no answer" already means "no".
  const reportableConsent = consentLanded ? consentDecision ?? undefined : undefined;

  // Device picker expanded state (collapsed by default in connection phase)
  const [deviceDrawerOpen, setDeviceDrawerOpen] = useState(false);

  // Track whether a local media-capture failure occurred during connect
  const [showCaptureErrorPrompt, setShowCaptureErrorPrompt] = useState(false);

  // Device selection -- only active after permission is granted and NOT in audio-only fallback
  const permissionGrantedForDevices =
    !env.demoMode &&
    (permissions.state === 'granted' || permissions.state === 'unsupported') &&
    permissions.camera !== 'denied';

  const deviceSelection = useDeviceSelection(permissionGrantedForDevices);

  // Connect to Twilio room based on permission state (skip in demo mode)
  // Connection starts immediately — consent only affects recording, not the connection itself
  useEffect(() => {
    if (env.demoMode) {
      const timer = setTimeout(() => onReady(), 3000);
      return () => clearTimeout(timer);
    }

    if (hasConnected) return;

    // Chat needs no Twilio room, and the room now survives this screen — connecting
    // here would leave a live one under a text-only consultation, with the camera
    // and microphone published to it. The customer's arrival is reported by App
    // instead (W7), and ConsultationScreen creates the room lazily if they later
    // switch to voice or video. "Connected" for the ceremony is the permission
    // answer, which is all this screen actually does for a chat customer.
    if (selectedMode === 'chat') {
      const permissionsAnswered =
        permissions.state === 'granted' ||
        permissions.state === 'unsupported' ||
        (permissions.camera === 'denied' && permissions.microphone === 'granted');
      // Skipping is an answer too, and the only one that costs the customer
      // nothing: chat needs no device, so there is nothing left to wait for.
      if (permissionsAnswered || permissionsSkipped) setHasConnected(true);
      return;
    }

    if (permissions.state === 'granted' || permissions.state === 'unsupported') {
      if (deviceSelection.isEnumerating) return;
      // Multi-device: wait for user to confirm device OR auto-confirm if drawer never opened
      if (deviceSelection.needsSelection && !userConfirmed && deviceDrawerOpen) return;

      // If multi-device but drawer not opened, auto-confirm with defaults
      if (deviceSelection.needsSelection && !userConfirmed && !deviceDrawerOpen) {
        setUserConfirmed(true);
      }

      // The room opened here is the room the consultation will use — it is no longer
      // rebuilt one screen later — so the camera is acquired only when the customer
      // asked for video. Voice-only used to reach that state on the second connect.
      const wantsVideo = selectedMode === 'video';
      const config: Parameters<typeof connect>[2] = { video: wantsVideo, audio: true };
      if (wantsVideo && deviceSelection.selectedCamera) {
        config.videoConstraints = { deviceId: { exact: deviceSelection.selectedCamera } };
      }
      if (deviceSelection.selectedMicrophone) {
        config.audioConstraints = { deviceId: { exact: deviceSelection.selectedMicrophone } };
      }

      connect(videoToken, roomName, config);
      setHasConnected(true);
    } else if (permissions.camera === 'denied' && permissions.microphone === 'granted') {
      connect(videoToken, roomName, { video: false, audio: true });
      setHasConnected(true);
      setResolvedMode('audio');
    }
  }, [
    videoToken,
    roomName,
    connect,
    onReady,
    selectedMode,
    permissions.state,
    permissions.camera,
    permissions.microphone,
    hasConnected,
    deviceSelection.isEnumerating,
    deviceSelection.needsSelection,
    deviceSelection.selectedCamera,
    deviceSelection.selectedMicrophone,
    userConfirmed,
    deviceDrawerOpen,
    permissionsSkipped,
  ]);

  // One-shot guard: a machine with no camera AND no microphone fails the audio-only
  // retry the same way, and without this the two would take turns forever.
  const audioOnlyFallbackTriedRef = useRef(false);

  // Detect local media-capture failures during the transition phase and surface the
  // device-error prompt instead of stranding the user on "Preparing your consultation..."
  useEffect(() => {
    if (!videoError) return;
    if (!videoError.isLocalCapture) return;

    // No camera on the machine is not an error the customer can act on — there is
    // no setting to flip and no other device to pick. Drop straight to audio-only
    // rather than blocking the entry with a modal whose every button says the same
    // thing. (Same line the Desktop app took for NotFoundError.)
    if (videoError.type === 'device-not-found' && !audioOnlyFallbackTriedRef.current) {
      audioOnlyFallbackTriedRef.current = true;
      setResolvedMode('audio');
      // hasConnected only after the audio-only attempt actually resolves: it arms the
      // advance timer, and setting it up front would walk a machine with no camera AND
      // no microphone straight into the consultation with no room behind it.
      void connect(videoToken, roomName, { video: false, audio: true })
        .then(() => setHasConnected(true))
        .catch(() => setShowCaptureErrorPrompt(true));
      return;
    }

    setShowCaptureErrorPrompt(true);
  }, [videoError, connect, videoToken, roomName]);

  // Advance to ConsultationScreen once connected AND consent resolved (if needed).
  // The second guard is the invariant of Q1 stated where it is enforced: no customer
  // reaches the consultation through this screen without having answered.
  useEffect(() => {
    if (env.demoMode || !hasConnected) return;
    if (needsConsent && consentDecision === null) return;

    const timer = setTimeout(
      () => onReady(resolvedMode ?? undefined, reportableConsent),
      2000
    );
    return () => clearTimeout(timer);
  }, [hasConnected, onReady, resolvedMode, needsConsent, consentDecision, reportableConsent]);

  // Latest isEnumerating value, read inside the watchdog timer callback without
  // re-subscribing the timer on every enumeration toggle.
  const isEnumeratingRef = useRef(deviceSelection.isEnumerating);
  useEffect(() => {
    isEnumeratingRef.current = deviceSelection.isEnumerating;
  }, [deviceSelection.isEnumerating]);

  // Watchdog: guarantee the client can never dead-end on the "Access granted /
  // Connecting you now…" beat. The connect effect gates setHasConnected(true) on
  // deviceSelection.isEnumerating; if that flag never clears, hasConnected never
  // flips and neither the connect nor the advance timer ever fire. This
  // independent max-wait forces a last-resort connect with browser-default
  // devices and drops the blocking confirmation, so the flow always advances
  // (via the advance effect once hasConnected is set).
  useEffect(() => {
    if (env.demoMode || hasConnected) return;
    // Only arm once we're past the permission prompt.
    if (permissions.state !== 'granted' && permissions.state !== 'unsupported') return;
    // Don't interrupt a user actively choosing devices in the picker drawer.
    if (deviceDrawerOpen) return;

    const timer = setTimeout(() => {
      // A device enumeration (e.g. an in-flight "Try Again") is still running —
      // don't force-connect on stale/default devices and discard its fresh
      // results. The isEnumerating dep re-arms this timer once enumeration
      // clears, giving the fresh enumeration a full window before we intervene.
      if (isEnumeratingRef.current) return;
      setShowGrantedConfirmation(false);
      connect(videoToken, roomName, { video: selectedMode === 'video', audio: true });
      setHasConnected(true);
    }, CONNECT_WATCHDOG_MS);
    return () => clearTimeout(timer);
  }, [
    hasConnected,
    permissions.state,
    deviceDrawerOpen,
    deviceSelection.isEnumerating,
    connect,
    videoToken,
    roomName,
    selectedMode,
  ]);

  // Handle consent
  const handleConsent = useCallback(async (consented: boolean) => {
    setConsentSubmitting(true);
    let landed = true;
    try {
      await apiClient.submitRecordingConsent(roomKey, consented);
    } catch (err) {
      landed = false;
      console.warn('[SPEC-026] Transcription consent API call failed:', err);
    }
    setConsentLanded(landed);
    // Recorded either way: the customer has answered, and holding them on the
    // question because of our network would be punishing them for it.
    setConsentDecision(consented);
    setConsentSubmitting(false);
    // Retire the "Access granted" beat for good (Q3). It reports the browser's
    // camera and microphone permission, not this answer, and the permission screens
    // raise the flag on their way here — so without this line the customer who has
    // just refused is congratulated on "Access granted" as the very next screen.
    // Nothing else can set the flag again from here: the two handlers that raise it
    // are both behind a permission prompt this customer has already passed.
    setShowGrantedConfirmation(false);
  }, [roomKey]);

  // Handle permission prompt state
  // One browser request at a time: a double click used to issue two, and every
  // unanswered one in Chrome counts towards its block.
  const requestInFlightRef = useRef(false);
  const requestOnce = async (): Promise<boolean | null> => {
    if (requestInFlightRef.current) return null;
    requestInFlightRef.current = true;
    try {
      return await permissions.requestPermissions();
    } finally {
      requestInFlightRef.current = false;
    }
  };

  const handleContinue = async () => {
    const granted = await requestOnce();
    // Show a brief "access granted" confirmation — only when access was granted.
    // The connection ceremony (hasConnected) handles the advance.
    if (granted && !env.demoMode) {
      setShowGrantedConfirmation(true);
    }
  };

  const handleTryAgain = async () => {
    // Belt and braces: the button is not even rendered while the browser is
    // refusing without a dialog (see `canTryAgain`). Read synchronously, so when
    // the request does go out it is still inside this click (Safari).
    if (permissions.isBlocked()) return;
    const granted = await requestOnce();
    if (granted === null) return;
    // Force a fresh device enumeration. The hook's one-shot guard only blocks the
    // auto permission-grant effect, so without this an in-page retry would never
    // re-enumerate and only a full page reload would recover.
    void deviceSelection
      .refreshDevices()
      .catch((e) => console.error('[Stream E] refreshDevices failed', e));
    if (granted && !env.demoMode) {
      setShowGrantedConfirmation(true);
    }
  };

  // Both "Continue with Chat" buttons — the chat branch of the blocked screen and
  // the video/audio one — land here, and this is the call that used to skip the
  // question entirely (Q1, hole A). It no longer can: the consent step returns
  // above every branch that renders these buttons, so an unanswered customer has
  // not been shown one to press.
  const handleContinueWithChat = useCallback(() => {
    onReady('chat', reportableConsent);
  }, [onReady, reportableConsent]);

  // MediaErrorPrompt handlers for capture failures during transition
  const handleCaptureErrorContinueWithoutVideo = useCallback(() => {
    setShowCaptureErrorPrompt(false);
    // A microphone failure has no audio-only left to fall back to: the modal now
    // says "Continue in chat" for it, and this is the branch that keeps that
    // promise. Retrying the same microphone one line below would fail identically
    // and land the customer in a voice consultation with no voice.
    if (videoError?.requested === 'microphone') {
      handleContinueWithChat();
      return;
    }
    // Fall back to audio-only (mirroring the camera-denied path)
    setResolvedMode('audio');
    if (!hasConnected) {
      connect(videoToken, roomName, { video: false, audio: true });
      setHasConnected(true);
    }
    // Carries the answer like every other exit from this screen. This one bypasses
    // the advance effect, so without it a customer who consented and then hit a
    // camera failure would arrive with the answer dropped — and the badge would tell
    // them their voice is out of a recording it is in.
    onReady('audio', reportableConsent);
  }, [
    hasConnected,
    connect,
    videoToken,
    roomName,
    onReady,
    reportableConsent,
    videoError,
    handleContinueWithChat,
  ]);

  const handleCaptureErrorRetry = useCallback(() => {
    setShowCaptureErrorPrompt(false);
    setHasConnected(false);
  }, []);

  const handleCaptureErrorChangeDevice = useCallback(() => {
    setShowCaptureErrorPrompt(false);
    // Re-open the device picker; the connect effect waits until the user confirms
    setUserConfirmed(false);
    setDeviceDrawerOpen(true);
    setHasConnected(false);
  }, []);

  // Render permission prompt screen (format-aware copy)
  if (!env.demoMode && permissions.state === 'prompt' && !permissionsSkipped) {
    const promptCopy = PROMPT_COPY[selectedMode];
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1, ease: 'easeInOut' }}
        className="min-h-screen w-full flex items-center justify-center bg-[#F8F6F0]"
      >
        <div className="max-w-lg px-6 text-center space-y-8">
          <div className="flex justify-center gap-6 text-[#C9A961]">
            <VideoIcon size={32} strokeWidth={1.2} />
            <MicIcon size={32} strokeWidth={1.2} />
          </div>

          <h1
            className="text-4xl text-[#1A1A2E] leading-relaxed tracking-wide font-serif font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {promptCopy.heading}
          </h1>

          <p className="text-lg text-[#1A1A2E]/80 leading-relaxed">
            {promptCopy.body}
          </p>

          {/* The dialog was closed, not answered: nothing is blocked, and
              Continue shows it again. Without this line the screen simply
              reappeared, as if the click had done nothing. */}
          {permissions.dismissed && (
            <p role="status" className="text-sm text-[#1A1A2E]/70 leading-relaxed italic">
              You closed the browser's request. Press Continue and choose Allow
              {selectedMode === 'chat' ? ', or go straight to the chat.' : '.'}
            </p>
          )}

          <div className="flex flex-col items-center gap-5">
            <button
              onClick={handleContinue}
              className="px-12 py-4 bg-[#C9A961] hover:bg-[#B89851] text-white font-serif text-lg tracking-wider transition-colors duration-300"
            >
              Continue
            </button>

            {/* The way out the copy above already promises. Chat needs no device,
                so asking the browser at all is the customer's choice — and every
                closed dialog brings Chrome's block one step nearer. */}
            {selectedMode === 'chat' && (
              <button
                onClick={() => setPermissionsSkipped(true)}
                className="text-sm text-[#1A1A2E]/50 tracking-wide hover:text-[#1A1A2E]/80 transition-colors duration-300 underline underline-offset-4"
              >
                Continue to chat without access
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Recording consent (Q1, va-qa-2026-08) ──
  //
  // Its own step, and deliberately so. Consent used to be a block inside the
  // connection ceremony, which is reached only with the microphone granted — so
  // every other way in walked straight past the question: both "Continue with Chat"
  // buttons call onReady directly, and the whole permissions.state === 'denied'
  // branch never renders the ceremony at all. Standing here, above all of them, it
  // is the single door into the consultation, for all three modes.
  //
  // It holds up nothing but the door. The connect effect above fires on the
  // permission answer, not on this one — consent governs the recording, never the
  // room — so the room is opening while the question is on screen. And it cannot
  // deadlock the way the inline version could (the comment two screens down
  // records how): being its own early return, no later screen can render over the
  // buttons that answer it.
  //
  // The condition is exactly "recording is on, this is not a demo, and nobody has
  // answered" — nothing about permissions. An earlier draft also excluded
  // permissions.state === 'checking', to keep the question from flashing ahead of
  // the permission prompt; that made the invariant depend on another hook's state
  // machine never resting in 'checking', which is a fact about useMediaPermissions
  // and not a property of this screen. It costs nothing to drop: 'checking' is the
  // initial value only, the permission prompt is checked above this line and wins
  // the moment it applies, and the one frame in between is spent at opacity 0 on
  // the way into the fade.
  if (!env.demoMode && needsConsent && consentDecision === null) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.8, ease: 'easeInOut' }}
        className="min-h-screen w-full flex items-center justify-center bg-[#F8F6F0]"
      >
        <div
          className="max-w-lg px-6 text-center space-y-8"
          role="region"
          aria-label="Recording consent"
        >
          {/* A statement, not a question: the room is recorded either way, and the
              recording has usually started before this screen is on. Offering a
              choice about it would be offering one that does not exist. */}
          <p
            className="text-base text-[#1A1A2E]/70 leading-relaxed"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            This consultation is recorded and transcribed.
          </p>

          <h1
            className="text-4xl text-[#1A1A2E] leading-relaxed tracking-wide font-serif font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            May we record your voice as well?
          </h1>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handleConsent(true)}
              disabled={consentSubmitting}
              className="px-8 py-3 bg-[#1A1A2E] text-[#F8F6F0] text-xs tracking-[0.2em] uppercase
                hover:bg-[#C9A961] disabled:opacity-50 disabled:cursor-not-allowed
                transition-all duration-500 border border-[#1A1A2E] hover:border-[#C9A961]"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Yes, record my voice
            </button>

            <button
              onClick={() => handleConsent(false)}
              disabled={consentSubmitting}
              className="px-8 py-3 border border-[#1A1A2E]/30 text-[#1A1A2E]/60 text-xs tracking-[0.2em] uppercase
                hover:border-[#C9A961] hover:text-[#C9A961] disabled:opacity-50 disabled:cursor-not-allowed
                transition-all duration-500"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              No, thank you
            </button>
          </div>

          {/* Not decoration. Without it "No, thank you" reads as "leave the
              consultation" — this is the line the whole rewrite is for. */}
          <p className="text-sm text-[#1A1A2E]/60 leading-relaxed italic">
            Either way you can speak and be heard — declining only keeps your voice
            out of the recording.
          </p>
        </div>
      </motion.div>
    );
  }

  // Brief "access granted" confirmation beat — shown after requestPermissions() resolves
  // granted or unsupported. Not shown in demo mode. Sits within the existing 3s ceremony.
  // Consent needs no guard here any more: it is answered above this line or the
  // customer never got past it. What does still matter is that answering clears
  // showGrantedConfirmation (see handleConsent), or this beat re-opens on top of the
  // answer — which was Q3.
  if (!env.demoMode && showGrantedConfirmation &&
      (permissions.state === 'granted' || permissions.state === 'unsupported')) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.6, ease: 'easeInOut' }}
        className="min-h-screen w-full flex items-center justify-center bg-[#F8F6F0]"
      >
        <div className="max-w-lg px-6 text-center space-y-6">
          <div className="flex justify-center text-[#C9A961]">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
              <circle cx="20" cy="20" r="18" stroke="#C9A961" strokeWidth="1.5" />
              <path d="M12 20l6 6 10-12" stroke="#C9A961" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p
            className="text-2xl text-[#1A1A2E] leading-relaxed tracking-wide font-serif font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Access granted
          </p>
          <p className="text-sm text-[#1A1A2E]/60 leading-relaxed">
            Connecting you now…
          </p>
        </div>
      </motion.div>
    );
  }

  // Determine if microphone is denied (makes audio/video modes unusable)
  const micDenied = permissions.microphone === 'denied';
  const cameraDenied = permissions.camera === 'denied';

  // Render blocked/denied state — copy differs based on selected format
  if (!env.demoMode && (micDenied || permissions.state === 'denied')) {
    const browser = getBrowserName();
    const bothBlocked = cameraDenied && micDenied;
    // Which device is actually blocked, not which one this screen is named after.
    // A video consultation reaches here on a camera block alone (the derived state
    // is 'denied' while the microphone is granted), and browser settings keep the
    // two on separate pages — naming the wrong one sends the customer somewhere
    // that will not fix anything.
    const blockedDevices: MediaRequest = bothBlocked
      ? 'both'
      : micDenied ? 'microphone' : 'camera';
    const instructions = getBrowserInstructions(blockedDevices, browser);
    // Offer "Try Again" only where pressing it can still produce a dialog. Once
    // the browser reports the block itself, the request is refused in silence —
    // the button did nothing, which read as a broken button. The instructions
    // above are the way out, and the permission is watched, so allowing access
    // there carries on by itself.
    const canTryAgain = !permissions.isBlocked(requiredDevices);

    if (selectedMode === 'chat') {
      // Reassuring copy: chat works regardless of mic/camera block
      const blockedNoun = bothBlocked
        ? 'camera and microphone are'
        : micDenied ? 'microphone is' : 'camera is';
      const chatBlockedBody =
        `Your ${blockedNoun} blocked, but that's fine — you're all set for chat. ` +
        `You can enable ${bothBlocked ? 'them' : 'it'} later in your browser settings if you want to switch to voice or video.`;

      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1, ease: 'easeInOut' }}
          className="min-h-screen w-full flex items-center justify-center bg-[#F8F6F0]"
        >
          <div className="max-w-lg px-6 text-center space-y-8">
            <h1
              className="text-4xl text-[#1A1A2E] leading-relaxed tracking-wide font-serif font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              You're all set for chat
            </h1>

            <p className="text-lg text-[#1A1A2E]/80 leading-relaxed">
              {chatBlockedBody}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleContinueWithChat}
                className="px-8 py-3 bg-[#C9A961] hover:bg-[#B89851] text-white font-serif text-base tracking-wider transition-colors duration-300"
              >
                Continue with Chat
              </button>

              {canTryAgain && (
                <button
                  onClick={handleTryAgain}
                  className="px-8 py-3 border-2 border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-white font-serif text-base tracking-wider transition-colors duration-300"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </motion.div>
      );
    }

    // video/audio modes: access is required — show guidance + browser instructions
    const deniedBodyText = bothBlocked
      ? 'Camera and microphone access is blocked. You can still connect via chat.'
      : micDenied
        ? 'Microphone access is blocked. You can still connect via chat.'
        : 'Camera access is blocked. You can still connect via chat.';
    const deniedHeading = bothBlocked
      ? 'Camera & Microphone Access Required'
      : micDenied ? 'Microphone Access Required' : 'Camera Access Required';

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1, ease: 'easeInOut' }}
        className="min-h-screen w-full flex items-center justify-center bg-[#F8F6F0]"
      >
        <div className="max-w-lg px-6 text-center space-y-8">
          <h1
            className="text-4xl text-[#1A1A2E] leading-relaxed tracking-wide font-serif font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {deniedHeading}
          </h1>

          <div className="space-y-4 text-left">
            <p className="text-lg text-[#1A1A2E]/80 leading-relaxed">
              {deniedBodyText}
            </p>

            <p className="text-base text-[#1A1A2E]/70 leading-relaxed pl-4 border-l-2 border-[#C9A961]">
              {instructions}
            </p>

            {/* No "refresh this page" here: the permission is watched, so access
                allowed from the address bar continues the entry by itself. */}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleContinueWithChat}
              className="px-8 py-3 bg-[#C9A961] hover:bg-[#B89851] text-white font-serif text-base tracking-wider transition-colors duration-300"
            >
              Continue with Chat
            </button>

            {canTryAgain && (
              <button
                onClick={handleTryAgain}
                className="px-8 py-3 border-2 border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-white font-serif text-base tracking-wider transition-colors duration-300"
              >
                Try Again
              </button>
            )}
          </div>

          {/* Last resort, and the quietest thing on the screen. */}
          <button
            onClick={() => window.location.reload()}
            className="text-xs text-[#1A1A2E]/40 tracking-wide hover:text-[#1A1A2E]/70 transition-colors duration-300 underline underline-offset-4"
          >
            Reload page
          </button>
        </div>
      </motion.div>
    );
  }

  // ── Connection ceremony: rings animation + compact device picker ──

  // Consent is settled by the time this renders — it has its own step above — so
  // the ceremony is waiting on the room and nothing else.
  const allResolved = hasConnected;
  const showDeviceOption =
    permissionGrantedForDevices &&
    !deviceSelection.isEnumerating &&
    deviceSelection.needsSelection;

  // Build device labels for compact display
  const selectedCameraLabel = deviceSelection.cameras.find(
    (c) => c.deviceId === deviceSelection.selectedCamera
  )?.label ?? 'Camera';
  const selectedMicLabel = deviceSelection.microphones.find(
    (m) => m.deviceId === deviceSelection.selectedMicrophone
  )?.label ?? 'Microphone';

  const multipleCameras = deviceSelection.cameras.length > 1;
  const multipleMics = deviceSelection.microphones.length > 1;

  // A voice consultation lists the microphone and nothing else. Naming a camera
  // here — even the placeholder label — contradicts the promise made two screens
  // ago that the camera stays off, and there is no camera permission behind it.
  const deviceSummary = requiredDevices === 'microphone'
    ? selectedMicLabel
    : `${selectedCameraLabel} · ${selectedMicLabel}`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1, ease: 'easeInOut' }}
      className="min-h-screen w-full flex items-center justify-center bg-[#F8F6F0]"
    >
      <div className="flex flex-col items-center space-y-12 max-w-md px-6">
        {/* Connection animation */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="relative flex items-center justify-center"
          style={{ width: 80, height: 80 }}
        >
          <motion.svg
            width="80"
            height="80"
            viewBox="0 0 100 100"
            className="absolute"
          >
            <motion.circle
              cx="50" cy="50" r="45"
              fill="none" stroke="#C9A961" strokeWidth="1"
              initial={{ opacity: 0.3 }}
              animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.circle
              cx="50" cy="50" r="30"
              fill="none" stroke="#C9A961" strokeWidth="1.5"
              initial={{ opacity: 0.5 }}
              animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
            />
            <circle cx="50" cy="50" r="4" fill="#C9A961" />
          </motion.svg>
        </motion.div>

        {/* Status text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1 }}
          className="text-center space-y-4"
        >
          <p
            className="text-3xl text-[#1A1A2E] leading-relaxed tracking-wide font-serif font-light"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {allResolved
              ? remoteParticipants.length > 0
                ? 'Your specialist is waiting for you'
                : 'Entering your consultation...'
              : 'Preparing your consultation...'}
          </p>

          {/* Audio-only fallback notice */}
          {resolvedMode === 'audio' && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="text-sm text-[#1A1A2E]/60 leading-relaxed italic"
            >
              Camera access is unavailable. Continuing with audio only.
            </motion.p>
          )}
        </motion.div>

        {/* Gold divider */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="w-12 h-px bg-[#C9A961]"
        />

        {/* ── Compact device selector ── */}
        <AnimatePresence>
          {showDeviceOption && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 1.4, duration: 0.6 }}
              className="text-center space-y-3"
            >
              {!deviceDrawerOpen ? (
                <>
                  <p
                    className="text-xs text-[#1A1A2E]/40 leading-relaxed tracking-wide"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {deviceSummary}
                  </p>
                  <button
                    onClick={() => setDeviceDrawerOpen(true)}
                    className="text-xs text-[#C9A961]/70 tracking-wide hover:text-[#C9A961] transition-colors duration-300 underline underline-offset-4"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Change
                  </button>
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-6 text-left max-w-sm"
                >
                  {/* Camera selection */}
                  {multipleCameras && (
                    <div className="space-y-3">
                      <h3
                        className="text-sm text-[#1A1A2E]/60 font-serif tracking-wide"
                        style={{ fontFamily: 'Cormorant Garamond, serif' }}
                      >
                        Camera
                      </h3>
                      <div className="space-y-2">
                        {deviceSelection.cameras.map((camera) => (
                          <label
                            key={camera.deviceId}
                            className="flex items-center gap-3 cursor-pointer group"
                          >
                            <div className="relative flex items-center justify-center">
                              <input
                                type="radio"
                                name="camera"
                                value={camera.deviceId}
                                checked={deviceSelection.selectedCamera === camera.deviceId}
                                onChange={() => deviceSelection.selectCamera(camera.deviceId)}
                                className="sr-only"
                              />
                              <div
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-200 ${
                                  deviceSelection.selectedCamera === camera.deviceId
                                    ? 'border-[#C9A961]'
                                    : 'border-[#1A1A2E]/20 group-hover:border-[#C9A961]/60'
                                }`}
                              >
                                {deviceSelection.selectedCamera === camera.deviceId && (
                                  <div className="w-2 h-2 rounded-full bg-[#C9A961]" />
                                )}
                              </div>
                            </div>
                            <span
                              className="text-sm text-[#1A1A2E]/70 leading-relaxed"
                              style={{ fontFamily: 'Montserrat, sans-serif' }}
                            >
                              {camera.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Microphone selection */}
                  {multipleMics && (
                    <div className="space-y-3">
                      <h3
                        className="text-sm text-[#1A1A2E]/60 font-serif tracking-wide"
                        style={{ fontFamily: 'Cormorant Garamond, serif' }}
                      >
                        Microphone
                      </h3>
                      <div className="space-y-2">
                        {deviceSelection.microphones.map((mic) => (
                          <label
                            key={mic.deviceId}
                            className="flex items-center gap-3 cursor-pointer group"
                          >
                            <div className="relative flex items-center justify-center">
                              <input
                                type="radio"
                                name="microphone"
                                value={mic.deviceId}
                                checked={deviceSelection.selectedMicrophone === mic.deviceId}
                                onChange={() => deviceSelection.selectMicrophone(mic.deviceId)}
                                className="sr-only"
                              />
                              <div
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-200 ${
                                  deviceSelection.selectedMicrophone === mic.deviceId
                                    ? 'border-[#C9A961]'
                                    : 'border-[#1A1A2E]/20 group-hover:border-[#C9A961]/60'
                                }`}
                              >
                                {deviceSelection.selectedMicrophone === mic.deviceId && (
                                  <div className="w-2 h-2 rounded-full bg-[#C9A961]" />
                                )}
                              </div>
                            </div>
                            <span
                              className="text-sm text-[#1A1A2E]/70 leading-relaxed"
                              style={{ fontFamily: 'Montserrat, sans-serif' }}
                            >
                              {mic.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-center pt-1">
                    <button
                      onClick={() => {
                        setUserConfirmed(true);
                        setDeviceDrawerOpen(false);
                      }}
                      className="px-8 py-2.5 text-xs tracking-[0.2em] uppercase text-[#C9A961] border border-[#C9A961]/40
                        hover:bg-[#C9A961] hover:text-white transition-all duration-500"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      Confirm
                    </button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>

      {/* Media capture error prompt — shown when the camera fails during the transition */}
      {videoError && videoError.isLocalCapture && (
        <MediaErrorPrompt
          isVisible={showCaptureErrorPrompt}
          error={videoError}
          onContinueWithoutVideo={handleCaptureErrorContinueWithoutVideo}
          onChangeDevice={handleCaptureErrorChangeDevice}
          onRetry={handleCaptureErrorRetry}
          canChangeDevice={deviceSelection.cameras.length > 1}
        />
      )}
    </motion.div>
  );
}
